// V4-FRONT-ANY-USAGE batch 5 2026-05-28
// V4-POS-MARKDOWN-PREVIEW 2026-06-22 — bascule preview markdown ⇄ édition brute.
import { useState, useRef, lazy, Suspense } from 'react';
import { FileText, Pencil } from 'lucide-react';
import type { TranslationKeys } from '../../../i18n/fr';

// Lazy : le stack markdown (react-markdown + remark-gfm) n'est chargé que lorsqu'on
// ouvre le mode Texte du positionnement (cf. V4-FRONT-BUNDLE-BLOAT).
const MarkdownPreview = lazy(() => import('../../common/MarkdownPreview'));

interface PositioningTextViewProps {
  editedPrompt: string;
  setEditedPrompt: (value: string) => void;
  positioningContext: string;
  setHasUnsavedChanges: (value: boolean) => void;
  isTemplate: boolean;
  t: TranslationKeys;
  renderToolbar: () => React.ReactNode;
  readOnly?: boolean;
}

/**
 * Vue texte du positionnement.
 * - Par défaut : aperçu markdown rendu (lisible). Cliquer dessus passe en édition.
 * - Édition : textarea markdown brut ; à la perte de focus, on revient à l'aperçu.
 * - Template (champs [entre crochets] à remplir) ou texte vide : on démarre en édition.
 * - Lecture seule (guest) : aperçu uniquement, non éditable.
 */
export function PositioningTextView({
  editedPrompt,
  setEditedPrompt,
  positioningContext,
  setHasUnsavedChanges,
  isTemplate,
  t,
  renderToolbar,
  readOnly = false,
}: PositioningTextViewProps) {
  const [isEditing, setIsEditing] = useState<boolean>(() => isTemplate || !editedPrompt?.trim());
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Lecture seule → jamais d'éditeur, on force l'aperçu.
  const showEditor = !readOnly && isEditing;

  const enterEdit = () => {
    if (readOnly) return;
    setIsEditing(true);
    // Focus après le rendu du textarea.
    setTimeout(() => textareaRef.current?.focus(), 0);
  };

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      <div className="flex-1 flex flex-col overflow-auto p-4">
        {isTemplate && (
          <div className="mb-4 p-3 rounded-lg flex items-center gap-2 flex-shrink-0" style={{ backgroundColor: 'rgba(59, 130, 246, 0.1)' }}>
            <FileText className="w-5 h-5" style={{ color: 'var(--color-primary)' }} />
            <span className="text-sm">
              {t.positioning?.templateInfo || "C'est un template par défaut. Remplissez les champs [entre crochets] et cochez les options correspondantes."}
            </span>
          </div>
        )}

        {showEditor ? (
          <textarea
            ref={textareaRef}
            value={editedPrompt}
            readOnly={readOnly}
            onChange={(e) => {
              if (readOnly) return;
              setEditedPrompt(e.target.value);
              setHasUnsavedChanges(e.target.value !== positioningContext);
            }}
            onBlur={(e) => {
              e.currentTarget.style.borderColor = 'var(--color-border)';
              // Repasser en aperçu en sortant de l'édition (sauf si vide → on reste éditable).
              if (editedPrompt?.trim()) setIsEditing(false);
            }}
            className="w-full flex-1 min-h-[300px] p-4 rounded-lg border-2 resize-none focus:outline-none"
            style={{
              backgroundColor: 'var(--color-surface)',
              borderColor: 'var(--color-border)',
              fontFamily: 'monospace',
              fontSize: '13px',
              lineHeight: '1.6'
            }}
            onFocus={(e) => e.currentTarget.style.borderColor = 'var(--color-primary)'}
            placeholder={t.positioning?.promptPlaceholder || 'Décrivez votre positionnement...'}
          />
        ) : (
          <div
            onClick={enterEdit}
            role={readOnly ? undefined : 'button'}
            tabIndex={readOnly ? undefined : 0}
            onKeyDown={(e) => {
              if (!readOnly && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); enterEdit(); }
            }}
            title={readOnly ? undefined : (t.positioning?.clickToEdit || 'Cliquer pour éditer le texte')}
            className="w-full flex-1 min-h-[300px] p-4 rounded-lg border-2 overflow-auto"
            style={{
              backgroundColor: 'var(--color-surface)',
              borderColor: 'var(--color-border)',
              cursor: readOnly ? 'default' : 'text',
            }}
          >
            {editedPrompt?.trim() ? (
              <Suspense fallback={<span className="text-sm" style={{ color: 'var(--color-text-muted)' }}>…</span>}>
                <MarkdownPreview content={editedPrompt} />
              </Suspense>
            ) : (
              <span className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
                {t.positioning?.promptPlaceholder || 'Décrivez votre positionnement...'}
              </span>
            )}
            {!readOnly && (
              <div className="flex items-center gap-1 mt-3 text-xs" style={{ color: 'var(--color-text-muted)' }}>
                <Pencil className="w-3 h-3" />
                {t.positioning?.clickToEdit || 'Cliquer pour éditer le texte'}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Toolbar en bas de la zone texte */}
      {renderToolbar()}
    </div>
  );
}
